import {
  canvasPageIdAtom,
  editorPagesAtom,
  isEditorReadyAtom,
} from '@/atoms/_editorAtoms'
import {
  selectedPageAtom,
  objectSelector,
  itemIdAtom,
  itemAtomFamily,
  pageLayoutAtom,
  watermarkIdAtom,
  watermarkAtomFamily,
} from 'atoms'
import { default as _omit } from 'lodash/omit'
import { useCallback } from 'react'
import {
  useRecoilValue,
  useRecoilCallback,
  useSetRecoilState,
  useRecoilTransaction_UNSTABLE,
} from 'recoil'
import { logger, EventCategory } from 'utils/logger'
import { loggerSentry } from '@/utils/loggerSentry'
import { useRecoilLogData } from 'utils/recoilLogger'
import { useEditorFlushTextBoxGeometry } from '@/hooks/editor/useEditorFlushTextBoxGeometry'
import { editorPageJsonItem } from '@/utils/editor-text-box/editorTextBoxAdapter'
import { logWipeProvenance } from '@/utils/editor/wipeProvenance'

/**
 * Pure decision helper extracted so tests can verify the guard without
 * spinning up Recoil. Returns `true` when `setEditorPages([])` would shrink
 * a populated page to empty — that's the wipe shape we refuse to persist.
 *
 * `null` / `undefined` inputs are treated as 0-length.
 */
export const wouldWipePage = (computedItems, currentItems) => {
  const computedLen = Array.isArray(computedItems) ? computedItems.length : 0
  const currentLen = Array.isArray(currentItems) ? currentItems.length : 0
  return computedLen === 0 && currentLen > 0
}

/**
 * Hydration-aware extension of {@link wouldWipePage}. The rebuilt-items write is
 * skipped ONLY when the editor has not finished loading the page yet
 * (`isEditorReady === false`) AND the write would shrink a populated page to
 * empty. Once the editor is hydrated, a populated → empty write is the user
 * deliberately clearing the page and MUST persist — keying on the wipe shape
 * alone (as before) silently swallowed that legitimate clear.
 */
export const shouldSkipItemsWrite = (
  isEditorReady,
  computedItems,
  currentItems,
) => !isEditorReady && wouldWipePage(computedItems, currentItems)

/**
 * True when the canvas atoms (itemIdAtom / itemAtomFamily) are known to hold a
 * DIFFERENT page than the one about to be written.
 *
 * Writes into `editorPagesAtom` address a page by array index
 * (`selectedPage - 1`) but take their content from the canvas, and the canvas is
 * hydrated in a post-render effect (Page.js `loadInitialData`). Between a
 * `selectedPage` change committing and that effect running, the index already
 * points at the new page while the canvas still holds the previous page's
 * items — persisting there clones page N-1's content onto page N while leaving
 * pageProperties/themeOverride intact. That is the Guest Book corruption:
 * a photo page keeping its own background but showing the preceding sign-in
 * page's content.
 *
 * `isEditorReadyAtom` cannot detect this — it stays true across navigation
 * (only a full editor re-init clears it) and the stale canvas is non-empty.
 *
 * Fails OPEN when either id is unknown: page JSON written before
 * `product.orderPageId` existed, and the pre-hydration mount, must still be
 * editable. Only a positive mismatch blocks.
 */
export const isCanvasStaleForPage = (canvasPageId, targetPageId) => {
  if (!canvasPageId || !targetPageId) return false
  return canvasPageId !== targetPageId
}

export const useUpdatePageItems = () => {
  const recoilLogData = useRecoilLogData()
  const selectedPage = useRecoilValue(selectedPageAtom)
  const flushEditorTextBoxGeometry = useEditorFlushTextBoxGeometry()

  const setEditorPages = useSetRecoilState(
    objectSelector({
      atom: editorPagesAtom,
      property: `[${selectedPage - 1}].product.items`,
    }),
  )

  // Collect the canvas items inside a transaction (reads the latest committed
  // state, including sets batched earlier in the same event handler). The
  // collected items are pushed into the caller-supplied array because Recoil's
  // transaction wrapper DISCARDS the inner function's return value — a
  // `return allItems` here reaches nobody, the caller receives `undefined`.
  // That silent undefined was the "items key absent" page wipe: it flowed into
  // setEditorPagesItems, landed as `product.items = undefined` on the page,
  // and JSON.stringify dropped the key from the save payload.
  const collectCanvasItemsTransaction = useRecoilTransaction_UNSTABLE(
    ({ get }) =>
      (collected) => {
        const ids = get(itemIdAtom)
        for (const id of ids) {
          const item = get(itemAtomFamily(id))
          if (item?.type) {
            collected.push(editorPageJsonItem(JSON.parse(JSON.stringify(item))))
          }
        }
      },
    [],
  )

  // Synchronous items-only sync for immediate updates (e.g., after image
  // edits). Transactions run synchronously, so the out-array is fully
  // populated before this returns — callers always get a real array.
  const syncItemsToEditorPages = useCallback(() => {
    const collected = []
    collectCanvasItemsTransaction(collected)
    return collected
  }, [collectCanvasItemsTransaction])

  // Guarded items-only write. Every image-edit entry point (Pintura, image
  // swap, quick actions) funnels through here via syncImageEditToEditorPages,
  // and each does seconds of async decode/re-encode before writing — a wide
  // window in which the user can change page. Without the stale check the write
  // lands on whatever page is selected when it resolves, carrying the canvas of
  // the page the edit started on.
  const setEditorPagesItems = useRecoilCallback(
    ({ snapshot }) =>
      (items) => {
        // A page's items are ALWAYS an array. Writing anything else corrupts
        // the slot invisibly: `product.items = undefined` renders fine (the
        // canvas atoms are untouched) but JSON.stringify drops the key from
        // the next save payload — the "items key absent" page wipe. Refuse
        // the write and page Sentry instead.
        if (!Array.isArray(items)) {
          logWipeProvenance(
            'editor_items_write_refused_non_array',
            'items write received a non-array value; write refused',
            {
              ...recoilLogData,
              writer: 'useUpdatePageItems.setEditorPagesItems',
              selectedPage,
              receivedType: items === null ? 'null' : typeof items,
            },
            'skipped',
          )
          return
        }

        const editorPages = snapshot.getLoadable(editorPagesAtom).valueMaybe()
        const targetPageId =
          editorPages?.[selectedPage - 1]?.product?.orderPageId
        const canvasPageId = snapshot.getLoadable(canvasPageIdAtom).valueMaybe()

        if (isCanvasStaleForPage(canvasPageId, targetPageId)) {
          // Rare and actionable: the refusal protects the page, but the user's
          // image edit is dropped, so this needs to be visible in Sentry — not
          // only in Datadog logs (`logger` is Datadog-only).
          const staleWriteError = new Error(
            'items write targeted a page the canvas is not hydrated for; write skipped',
          )
          const staleWriteContext = {
            ...recoilLogData,
            selectedPage,
            canvasPageId,
            targetPageId,
          }
          logger.error({
            message: 'editor_items_write_skipped_stale_canvas',
            category: EventCategory.ERROR,
            error: staleWriteError,
            data: staleWriteContext,
          })
          loggerSentry.error({
            message: 'editor_items_write_skipped_stale_canvas',
            category: EventCategory.ERROR,
            error: staleWriteError,
            data: staleWriteContext,
          })
          return
        }

        // Same un-hydrated wipe guard as saveItems: during the editor re-init
        // window itemIdAtom is [], so an image edit resolving in that window
        // collects zero items — and canvasPageId is reset by the same re-init,
        // so the stale check above fails open. Persisting that [] over a
        // populated page is the image-edit road to the page wipe. A hydrated
        // populated → empty write still passes (an image edit never empties a
        // page on its own, but the rule stays symmetric with saveItems).
        const isEditorReady = snapshot
          .getLoadable(isEditorReadyAtom)
          .valueMaybe()
        const currentItems =
          editorPages?.[selectedPage - 1]?.product?.items ?? []
        if (shouldSkipItemsWrite(isEditorReady, items, currentItems)) {
          logWipeProvenance(
            'editor_items_write_skipped_empty',
            'items write would wipe a populated page before hydration; write skipped',
            {
              ...recoilLogData,
              writer: 'useUpdatePageItems.setEditorPagesItems',
              selectedPage,
              isEditorReady,
              computedItemsLength: items.length,
              currentItemsLength: currentItems.length,
            },
            'skipped',
          )
          return
        }

        setEditorPages(items)
      },
    [selectedPage, setEditorPages, recoilLogData],
  )

  const saveItems = useRecoilCallback(({ snapshot, set }) => async () => {
    const release = snapshot.retain()
    try {
      const ids = await snapshot.getPromise(itemIdAtom)
      const allItems = []

      for (const id of ids) {
        const item = await snapshot.getPromise(itemAtomFamily(id))
        if (item?.type) {
          allItems.push(editorPageJsonItem(JSON.parse(JSON.stringify(item))))
        }
      }

      // itemIdAtom resets to [] during the editor re-init window (a layout
      // swap / page-set reload via useResetEditorAtoms) until Page.js's mount
      // effect rehydrates it — ordinary page nav swaps ids old → new with no
      // empty state. Persisting that transient [] would wipe the current
      // page. Skip the write when it would shrink populated → empty.
      const editorPages = await snapshot.getPromise(editorPagesAtom)
      const targetPage = editorPages?.[selectedPage - 1]
      const targetPageId = targetPage?.product?.orderPageId
      const canvasPageId = await snapshot.getPromise(canvasPageIdAtom)

      // The canvas still holds a different page than the one this flush targets
      // — persisting it would clone that page's content onto this one. Drop the
      // whole flush (themeOverride and watermarks would be equally stale);
      // Page.js's hydration effect re-stamps and the next flush is safe.
      if (isCanvasStaleForPage(canvasPageId, targetPageId)) {
        const staleFlushError = new Error(
          'saveItems targeted a page the canvas is not hydrated for; write skipped',
        )
        const staleFlushContext = {
          ...recoilLogData,
          selectedPage,
          canvasPageId,
          targetPageId,
        }
        logger.error({
          message: 'editor_saveitems_skipped_stale_canvas',
          category: EventCategory.ERROR,
          error: staleFlushError,
          data: staleFlushContext,
        })
        loggerSentry.error({
          message: 'editor_saveitems_skipped_stale_canvas',
          category: EventCategory.ERROR,
          error: staleFlushError,
          data: staleFlushContext,
        })
        return
      }

      const currentItems = targetPage?.product?.items ?? []
      const isEditorReady = await snapshot.getPromise(isEditorReadyAtom)
      // Skip the write ONLY during the un-hydrated re-init window. A hydrated
      // populated → empty write is an intentional clear and falls through to
      // persist normally — see shouldSkipItemsWrite.
      const skipWrite = shouldSkipItemsWrite(
        isEditorReady,
        allItems,
        currentItems,
      )
      if (skipWrite) {
        logger.error({
          message: 'editor_saveitems_skipped_empty',
          category: EventCategory.ERROR,
          error: new Error(
            'saveItems would wipe a populated page before hydration; write skipped',
          ),
          data: {
            ...recoilLogData,
            selectedPage,
            isEditorReady,
            itemIdAtomLength: ids.length,
            currentItemsLength: currentItems.length,
          },
        })
        // The un-hydrated working copy is untrusted IN FULL: pageLayoutAtom
        // and watermarkIdAtom were reset by the same re-init that dropped the
        // ready flag, so continuing would clobber the populated page's
        // themeOverride/watermarks with defaults — the mirror image of the
        // items wipe this guard exists to stop. Skip the whole flush.
        return
      }
      // OBSERVABILITY (log-only, the write below proceeds unchanged): a
      // wipe-shaped flush that PASSED both guards — hydrated editor, canvas
      // on the right page, yet zero collected items over a populated page —
      // is the state no local reproduction has explained. Emit full
      // provenance so a field occurrence names this writer and its atom
      // state; correlate with the backend suspicious_page_wipe audit row via
      // page id + timestamp.
      if (wouldWipePage(allItems, currentItems)) {
        logWipeProvenance(
          'editor_saveitems_wipe_shaped_stamp',
          'saveItems stamped a wipe-shaped payload past both guards',
          {
            ...recoilLogData,
            writer: 'useUpdatePageItems.saveItems',
            selectedPage,
            canvasPageId,
            targetPageId,
            isEditorReady,
            itemIdAtomLength: ids.length,
            currentItemsLength: currentItems.length,
          },
        )
      }
      await flushEditorTextBoxGeometry()

      const watermarks = await snapshot.getPromise(watermarkIdAtom)
      const watermarkItems = []
      for (const id of watermarks) {
        const item = await snapshot.getPromise(watermarkAtomFamily(id))
        if (item?.type) {
          watermarkItems.push(JSON.parse(JSON.stringify(item)))
        }
      }

      const updatedThemeOverride = await snapshot.getPromise(pageLayoutAtom)

      const omit = [
        'archived',
        'available',
        'id',
        'layout_id',
        'paper_type',
        'product_id',
      ]

      const themeOverride = _omit(updatedThemeOverride, omit)

      // One write for the page, and the written array is handed back: a
      // Recoil set lands in the store's next tree, and a snapshot taken by
      // the caller after this promise resolves still reads the current one
      // (Recoil 0.5, `cloneSnapshot`), so a Save that read the atom after
      // this flush sent the canvas as it stood before it — every edit since
      // the last sync missed the row and landed on the Save after. The
      // caller sends what it is handed.
      const updatedPages = editorPages.map((page, index) =>
        index === selectedPage - 1
          ? {
              ...page,
              product: {
                ...page.product,
                items: allItems,
                ...(watermarkItems.length > 0
                  ? { watermarks: watermarkItems }
                  : {}),
                themeOverride,
              },
            }
          : page,
      )
      set(editorPagesAtom, updatedPages)

      logger.info({
        message: 'editor_save_items_success',
        category: EventCategory.SAVE,
        data: {
          ...recoilLogData,
          skippedWipe: skipWrite,
        },
      })
      return updatedPages
    } catch (error) {
      logger.error({
        message: 'editor_save_failed',
        category: EventCategory.ERROR,
        error: error,
        data: {
          ...recoilLogData,
        },
      })
      // A guard's skip resolves undefined, which tells a Save to send the
      // pages atom; a failure must not, because the canvas holds edits the
      // atom has never seen, and the Save would send the page without them
      // and report success.
      throw error
    } finally {
      release()
    }
  })

  /**
   * @returns {Object} Object with saveItems, syncItemsToEditorPages, and setEditorPagesItems
   * @property {() => Promise<any[]|undefined>} saveItems - Async full sync (items + watermarks + themeOverride); resolves to the pages array it wrote, or undefined when a guard skipped the write; rejects when the sync itself failed
   * @property {() => any[]} syncItemsToEditorPages - Synchronous items-only sync, returns items array
   * @property {(items: any[]) => void} setEditorPagesItems - Setter for items array
   */
  return {
    // Async full sync (items + watermarks + themeOverride) for save/navigation operations
    saveItems: () => saveItems(),
    // Synchronous items-only sync for immediate updates (e.g., after image edits)
    syncItemsToEditorPages,
    // Setter for items array (used by syncItemsToEditorPages)
    // Note: Recoil transactions cannot set selectors directly.
    // The pattern: syncItemsToEditorPages() reads state inside a transaction and returns the items array,
    // then setEditorPagesItems() sets that array outside the transaction. The hook provides the correctly-scoped
    // setter for the current page (uses selectedPage to build the selector path), so this is the appropriate place to expose it.
    // The write refuses to land when the canvas belongs to a different page than
    // the one selected — see isCanvasStaleForPage.
    setEditorPagesItems,
  }
}

export default useUpdatePageItems
